import React, { useEffect, useState } from 'react';
import { Sparkles, AlertTriangle, RefreshCw, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { LotsTable } from './LotsTable';

const SELECTS = {
  type_operation: [['construction_neuve', 'Construction neuve'], ['renovation_lourde', 'Rénovation lourde'], ['rehabilitation', 'Réhabilitation'], ['extension', 'Extension'], ['surelevation', 'Surélévation']],
  type_batiment: [['individuel', 'Individuel'], ['collectif', 'Collectif'], ['mixte', 'Mixte'], ['tertiaire', 'Tertiaire']],
  territoire: [['guadeloupe', 'Guadeloupe'], ['martinique', 'Martinique'], ['guyane', 'Guyane'], ['reunion', 'La Réunion'], ['mayotte', 'Mayotte'], ['saint_martin', 'Saint-Martin'], ['saint_barthelemy', 'Saint-Barthélemy']],
};
const ALIAS = { renovation: 'renovation_lourde', la_reunion: 'reunion' };
const normaliserForm = (form) => {
  const op = { ...(form?.operation || {}) };
  Object.keys(SELECTS).forEach((k) => { if (ALIAS[op[k]]) op[k] = ALIAS[op[k]]; });
  return { ...(form || {}), operation: op };
};

const SECTIONS = [
  ['Identité du demandeur', 'identite', [['nom', 'Nom'], ['prenom', 'Prénom'], ['raison_sociale', 'Raison sociale'], ['siret', 'SIRET'], ['email', 'Email'], ['telephone', 'Téléphone'], ['adresse', 'Adresse'], ['code_postal', 'Code postal'], ['ville', 'Ville']]],
  ['Opération', 'operation', [['type_operation', "Type d'opération"], ['type_batiment', 'Type de bâtiment'], ['territoire', 'Territoire'], ['adresse_chantier', 'Adresse chantier'], ['code_postal_chantier', 'CP chantier'], ['ville_chantier', 'Ville chantier'], ['reference_permis', 'Réf. permis'], ['description_projet', 'Description projet']]],
  ['Données techniques', 'technique', [['cout_construction_ht', 'Coût construction HT (€)'], ['surface_plancher', 'Surface plancher (m²)'], ['duree_travaux_mois', 'Durée travaux (mois)'], ['part_entreprises_ess', 'Part entreprises ESS (%)'], ['date_debut_travaux', 'Début des travaux'], ['nombre_niveaux', 'Nombre de niveaux']]],
];

const FieldInput = ({ section, k, value, onChange, missing }) => {
  const id = `ia-field-${section}-${k}`;
  const cls = `h-9 ${missing ? 'border-amber-400 bg-amber-50/50' : ''}`;
  if (SELECTS[k]) {
    return (
      <select value={value || ''} onChange={(e) => onChange(e.target.value)} className={`w-full rounded-md border border-input bg-white px-3 text-sm ${cls}`} data-testid={id}>
        <option value="">—</option>
        {SELECTS[k].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    );
  }
  const type = k === 'date_debut_travaux' ? 'date' : 'text';
  return <Input type={type} value={value ?? ''} onChange={(e) => onChange(e.target.value)} className={cls} data-testid={id} title={String(value ?? '')} />;
};

export const ExtractedDataCard = ({ session, saving, onSave }) => {
  const [form, setForm] = useState(normaliserForm(session.form));
  const [rows, setRows] = useState(session.intervenants || []);
  useEffect(() => { setForm(normaliserForm(session.form)); setRows(session.intervenants || []); }, [session]);
  const missingKeys = new Set((session.manquants || []).map((m) => m.key));
  const set = (sec, k, v) => setForm((f) => ({ ...f, [sec]: { ...(f[sec] || {}), [k]: v } }));
  const alertes = [...(session.alertes_auto || []), ...(session.alertes_ia || [])];

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5 space-y-4" data-testid="ia-extracted-card">
      <h4 className="flex items-center gap-2 text-sm font-semibold text-slate-800"><Sparkles className="h-4 w-4 text-violet-600" /> 2. Données extraites (vérifiez et corrigez)</h4>
      {session.resume && <p className="text-sm text-slate-700 bg-violet-50/60 rounded-lg p-3" data-testid="ia-resume">{session.resume}</p>}
      {alertes.length > 0 && (
        <ul className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-1 max-h-60 overflow-y-auto" data-testid="ia-alertes">
          {alertes.map((a, i) => (
            <li key={i} className={`flex gap-1.5 text-xs ${i < (session.alertes_auto || []).length ? 'text-red-700 font-medium' : 'text-amber-800'}`}><AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" /> {a}</li>
          ))}
        </ul>
      )}
      {SECTIONS.map(([title, sec, fields]) => (
        <div key={sec} className="space-y-2">
          <h5 className="text-sm font-semibold text-slate-700">{title}</h5>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {fields.map(([k, label]) => {
              const missing = missingKeys.has(`${sec}.${k}`) || (k === 'nom' && missingKeys.has('identite.nom'));
              return (
                <label key={k} className="text-xs text-slate-600 space-y-1">
                  <span>{label}{missing && <span className="text-amber-600"> • manquant</span>}</span>
                  <FieldInput section={sec} k={k} value={(form[sec] || {})[k]} onChange={(v) => set(sec, k, v)} missing={missing} />
                </label>
              );
            })}
          </div>
        </div>
      ))}
      <LotsTable rows={rows} onChange={setRows} />
      <Button variant="outline" onClick={() => onSave({ form, intervenants: rows })} disabled={saving} className="gap-2" data-testid="ia-save-recalc-btn">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Enregistrer et recalculer
      </Button>
    </section>
  );
};
